import { createApi } from '@reduxjs/toolkit/query/react'

import { updateEventDataToFirebase } from '~/gamification/apis/gamification.api'
import { setHasLeveledUp, updateEventsActionCreator } from '~/gamification/slices/gamification.slice'
import {
  EventData,
  EventResponse,
  GamificationRewards,
  GamificationRewardsRequest,
} from '~/gamification/types/gamification.types'
import { apiPrivate } from '~/redux/utils/hom-api'

import { RewardsStats } from '~/statistics/types/profile-card.types'

import { CheckListTypes, RewardsEventIds } from './types'

import { DateFilters } from './user-stats-api'

export const rewardApi = createApi({
  reducerPath: 'rewardApi',
  tagTypes: ['Rewards'],
  baseQuery: apiPrivate,
  endpoints: (builder) => ({
    getRewardsByFeature: builder.query<RewardsEventIds[], { feature: string }>({
      query: ({ feature }) => ({
        url: `/v1/reward/feature/${feature}`,
        method: 'GET',
      }),
      providesTags: ['Rewards'],
    }),
    getRewards: builder.query<GamificationRewards, void | { userId: number }>({
      query: () => ({
        url: '/v1/reward',
        method: 'GET',
      }),
      providesTags: ['Rewards'],
    }),
    getFamilyMemberRewards: builder.query<GamificationRewards, { familyMemberId?: number }>({
      query: ({ familyMemberId }) => ({
        url: `/v1/family-member-rewards${familyMemberId ? `?familyMemberId=${familyMemberId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Rewards'],
    }),
    updateEventData: builder.mutation<EventResponse, EventData[]>({
      queryFn: async (events, { dispatch }) => {
        dispatch(updateEventsActionCreator({ events }))
        dispatch(setHasLeveledUp(false))

        return { data: (await updateEventDataToFirebase(events)) as unknown as EventResponse }
      },
      invalidatesTags: ['Rewards'],
    }),
    setRewards: builder.mutation<{ type: 'success' | 'error' }, GamificationRewardsRequest>({
      query: ({ gems = 0, xp = 0, kudos = 0, feature, event_id }) => ({
        url: '/v1/reward',
        method: 'POST',
        body: {
          gems,
          xp,
          kudos,
          feature,
          event_id,
        },
      }),
      invalidatesTags: ['Rewards'],
    }),
    getChecklist: builder.query<CheckListTypes, void>({
      query: () => ({
        url: '/v1/checklist',
        method: 'GET',
      }),
      providesTags: ['Rewards'],
    }),
    setChecklistRewards: builder.mutation<{ type: 'success' | 'error' }, GamificationRewardsRequest>({
      query: ({ gems = 0, xp = 0, kudos = 0, feature, event_id }) => ({
        url: '/v1/checklist-reward',
        method: 'POST',
        body: {
          gems,
          xp,
          kudos,
          feature,
          event_id,
        },
      }),
      invalidatesTags: ['Rewards'],
    }),
    rewardStats: builder.query<RewardsStats, DateFilters>({
      query: (params) => ({
        url: '/v1/reward/user-stats',
        method: 'GET',
        params,
      }),
      providesTags: ['Rewards'],
    }),
  }),
})

export const {
  useGetRewardsQuery,
  useGetFamilyMemberRewardsQuery,
  useSetRewardsMutation,
  useUpdateEventDataMutation,
  useGetChecklistQuery,
  useSetChecklistRewardsMutation,
  useGetRewardsByFeatureQuery,
  useRewardStatsQuery,
} = rewardApi
